import React, { useEffect, useState, useRef } from 'react';
import { onPartyPreviewsUpdate } from '../../controller/PartyDB';
import { IPartyPreview } from '../../Party';
import {
  CaretRightOutlined,
  CaretLeftOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
} from '@ant-design/icons';
import _ from 'lodash';
import getSortedPartyList from './getSortedPartyList';
import { useAppState } from '../../state/AppState';

const SCROLL_AMOUNT_PX = 300;
const DEFAULT_PREVIEW_IMAGE_URL = '/backroom_logo.svg';

const PartyExplorer = () => {
  const [partyPreviews, setPartyPreviews] = useState<Record<string, IPartyPreview>>({});
  useEffect(() => {
    const detachListener = onPartyPreviewsUpdate(async (updatedPartyPreviews) => {
      if (updatedPartyPreviews) {
        setPartyPreviews(updatedPartyPreviews);
      }
    });
    const willUnmount = () => {
      detachListener();
    };
    return willUnmount;
  }, []);

  const { party } = useAppState();
  const ref = useRef<HTMLDivElement>(null);
  const [, forceUpdate] = useState<number>();
  const isFullyScrolledLeft = ref.current?.scrollLeft === 0;
  const isFullyScrolledRight =
    ref.current?.scrollWidth ===
    (ref.current?.scrollLeft || 0) + (ref.current?.children[0].clientWidth || 0);

  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
  const collapseIcon = isCollapsed ? <CaretDownOutlined /> : <CaretUpOutlined />;

  useEffect(() => {
    if (ref.current && !isCollapsed) {
      ref.current.scrollLeft = 0;
      forceUpdate(Math.random());
    }
  }, [isCollapsed]);

  const sortedPartyList = getSortedPartyList(partyPreviews, party?.key);

  return (
    <div className='party-explorer-container'>
      <div className={`party-preview-container ${isCollapsed ? 'collapsed' : ''}`}>
        <CaretLeftOutlined
          style={{
            transform: 'translateX(-100%)',
            fontSize: 72,
            position: 'absolute',
            opacity: isFullyScrolledLeft ? 0 : 1,
            transition: 'opacity linear 0.2s',
            color: '#e45683',
          }}
          onClick={() => {
            if (ref.current) {
              ref.current.scrollBy({ left: -SCROLL_AMOUNT_PX, behavior: 'smooth' });
              // wait for window redraw to force update
              // this make sure we can accurately check scrollLeft elsewhere here
              setTimeout(() => forceUpdate(Math.random()), 200);
            }
          }}
        />
        <div ref={ref} style={{ overflowX: 'hidden', position: 'relative' }}>
          <div style={{ display: 'flex' }}>
            {sortedPartyList.map((preview: IPartyPreview) => (
              <div style={{ padding: 20 }} key={preview.key}>
                <a href={`/${preview.slug}`}>
                  <div
                    style={{
                      backgroundImage: `url("${
                        preview.previewImageUrl || DEFAULT_PREVIEW_IMAGE_URL
                      }")`,
                      backgroundPosition: 'center',
                      backgroundSize: 'cover',
                      backgroundRepeat: 'no-repeat',
                      height: 100,
                      width: 100,
                      borderRadius: '50%',
                      position: 'relative',
                    }}>
                    {!!Object.values(preview.participants || {}).length && (
                      <div
                        style={{
                          color: 'white',
                          padding: 6,
                          position: 'absolute',
                          left: 0,
                          bottom: 0,
                          width: 35,
                          height: 35,
                          fontWeight: 'bolder',
                          borderRadius: '50%',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          textShadow: '0 0 7px white',
                          fontSize: 20,
                        }}>
                        {Object.values(preview.participants || {}).length}
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      fontWeight: 'bold',
                      fontSize: 16,
                      paddingTop: 8,
                      maxWidth: 160,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                    <span>{preview.slug}</span>
                  </div>
                </a>
              </div>
            ))}
          </div>
        </div>
        <CaretRightOutlined
          style={{
            transform: 'translateX(100%)',
            fontSize: 72,
            position: 'absolute',
            right: 0,
            opacity: isFullyScrolledRight ? 0 : 1,
            transition: 'opacity linear 0.2s',
            color: '#e45683',
          }}
          onClick={() => {
            if (ref.current) {
              ref.current.scrollBy({ left: SCROLL_AMOUNT_PX, behavior: 'smooth' });
              // wait for window redraw to force update
              // this make sure we can accurately check scrollLeft elsewhere here
              setTimeout(() => forceUpdate(Math.random()), 200);
            }
          }}
        />
      </div>
      <div style={{ justifyContent: 'center', display: 'flex' }}>
        <div className={`curved-corner-topright ${isCollapsed ? 'collapsed' : ''}`}></div>
        <div
          className={`collapse-party-explorer ${isCollapsed ? 'collapsed' : ''}`}
          onClick={() => setIsCollapsed((ic) => !ic)}>
          {collapseIcon}
          <span style={{ paddingLeft: 12, paddingRight: 12 }}>Parties Live Now</span>
          {collapseIcon}
        </div>
        <div className={`curved-corner-topleft ${isCollapsed ? 'collapsed' : ''}`}></div>
      </div>
    </div>
  );
};

export default PartyExplorer;
